/** Change company or financial year without signing out.
 *
 * AUTH_DESIGN.md §7.3: this updates the session record. **No new token is
 * issued**, because the context was never in the token — which is precisely
 * why switching is cheap and why a client cannot forge it.
 *
 * The permission cache on the server is keyed on user + company and is
 * invalidated by the switch, so rights re-resolve against the new company
 * immediately rather than lingering from the old one.
 */

import { useEffect, useRef, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import type { Session } from '@/types'

export function ContextSwitcher(
  { session, onSwitched, onClose }: {
    session: Session
    onSwitched: (companyId: string, companyName: string, year: string,
                 yearStatus: 'Open' | 'Closed') => void
    onClose: () => void
  },
) {
  const [companyId, setCompanyId] = useState(session.companyId)
  const [year, setYear] = useState(session.financialYear)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const first = useRef<HTMLSelectElement>(null)

  useEffect(() => {
    first.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Years belong to the company, so switching company re-picks the year.
  const years = session.availableYears
  const chosenYear = years.find(y => y.financialYear === year)
  const changed = companyId !== session.companyId || year !== session.financialYear

  async function apply(e: React.FormEvent) {
    e.preventDefault()
    if (!changed) { onClose(); return }
    setBusy(true); setError(null)
    try {
      await api.create('/auth/switch',
                       { company_id: companyId, financial_year: year })
      const company = session.availableCompanies.find(c => c.companyId === companyId)!
      onSwitched(companyId, company.name, year, chosenYear?.status ?? 'Open')
    } catch (err) {
      setError(err instanceof ApiError ? err.message
                                       : 'The context could not be changed.')
    } finally { setBusy(false) }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={apply}
            role="dialog" aria-modal="true" aria-label="Switch company or year">
        <div className="card-header">Which books are you working in?</div>

        {error && <div className="form-errors" role="alert">{error}</div>}

        <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
          <div className="form-field">
            <label htmlFor="sw-c">Company</label>
            {/* Native select, deliberately. UI_TASTE.md: selects inside a
                modal stay native — an overlay combobox misbehaves here, and
                that was learned the hard way on the CRM. */}
            <select id="sw-c" ref={first} value={companyId}
                    onChange={e => setCompanyId(e.target.value)}>
              {session.availableCompanies.map(c => (
                <option key={c.companyId} value={c.companyId}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="sw-y">Financial Year</label>
            <select id="sw-y" value={year} onChange={e => setYear(e.target.value)}>
              {years.map(y => (
                <option key={y.financialYear} value={y.financialYear}>
                  {y.financialYear}{y.status === 'Closed' ? ' — closed' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {chosenYear?.status === 'Closed' && (
          <p className="form-note" style={{ color: 'var(--text-warning)' }}>
            {year} is closed — you will be able to read and print history,
            but not create or change records.
          </p>
        )}

        {companyId !== session.companyId && (
          <p className="form-note">
            Switching company changes every list, document and permission in
            the application. Your rights in {
              session.availableCompanies.find(c => c.companyId === companyId)?.name
            } may differ from those you hold here.
          </p>
        )}

        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy}>
            {busy ? 'Switching…' : changed ? 'Switch' : 'Close'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
